/**
 * @license
 * Copyright 2024 takni.xyz
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { DesignModeProvider, useDesignMode } from './context/DesignModeContext';
import { RainbowKitThemeProvider } from './components/RainbowKitThemeProvider';
import { LanguagePreferenceProvider } from './context/LanguagePreferenceContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { themeCornerIsChamfer, isDarkDesignMode, PUBLIC_DESIGN_MODES, DEFAULT_DESIGN_MODE } from './config/theme';
import { isAdminWallet } from './config/adminWallet';
import { HomeViewModeProvider } from './context/HomeViewModeContext';
import Navbar from './components/Navbar';
import GlobalTickerBanner from './components/GlobalTickerBanner';
import Footer from './components/Footer';
import WelcomeModal from './components/WelcomeModal';
import LoadingSpinner from './components/LoadingSpinner';

/** Preserves bookmarks / shared links from the pre-rename URL scheme. */
function LegacyMarketRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/market/${id ?? ''}`} replace />;
}

const RECONNECT_WAIT_MS = 4_000;

/**
 * Gate wallet-dependent pages: redirect to Home when disconnected.
 * Waits briefly for wagmi reconnect, but times out so routes do not hang forever.
 */
function RequireWallet({ children }: { children: React.ReactNode }) {
  const { isConnected, isConnecting, isReconnecting, status } = useAccount();
  const [reconnectTimedOut, setReconnectTimedOut] = useState(false);

  const waitingReconnect =
    !reconnectTimedOut &&
    (status === 'connecting' || status === 'reconnecting' || isConnecting || isReconnecting);

  useEffect(() => {
    if (!waitingReconnect) {
      setReconnectTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setReconnectTimedOut(true), RECONNECT_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [waitingReconnect]);

  if (waitingReconnect) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <LoadingSpinner />
      </div>
    );
  }
  if (!isConnected) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Explore is public preview only — redirect connected users to the main app home.
 */
function RequireNoWallet({ children }: { children: React.ReactNode }) {
  const { isConnected, isConnecting, isReconnecting, status } = useAccount();
  const [reconnectTimedOut, setReconnectTimedOut] = useState(false);

  const waitingReconnect =
    !reconnectTimedOut &&
    (status === 'connecting' || status === 'reconnecting' || isConnecting || isReconnecting);

  useEffect(() => {
    if (!waitingReconnect) {
      setReconnectTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setReconnectTimedOut(true), RECONNECT_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [waitingReconnect]);

  if (waitingReconnect) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <LoadingSpinner />
      </div>
    );
  }
  if (isConnected) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const Home = lazy(() => import('./pages/Home.tsx'));
const PublicPortal = lazy(() => import('./pages/PublicPortal.tsx'));
const CreateMarket = lazy(() => import('./pages/CreateMarket.tsx'));
const MarketDetail = lazy(() => import('./pages/MarketDetail.tsx'));
const Profile = lazy(() => import('./pages/Profile.tsx'));
const Admin = lazy(() => import('./pages/Admin.tsx'));
const Docs = lazy(() => import('./pages/Docs.tsx'));
const Diagnose = lazy(() => import('./pages/Diagnose.tsx'));
const DailyDigest = lazy(() => import('./pages/DailyDigest.tsx'));
const NotFound = lazy(() => import('./pages/NotFound.tsx'));

/** Non-admins may only use public palettes; reset legacy stored modes on load. */
function DesignModeAccessGuard() {
  const { address } = useAccount();
  const { designMode, setDesignMode } = useDesignMode();
  const isAdmin = isAdminWallet(address);

  useEffect(() => {
    if (!isAdmin && !PUBLIC_DESIGN_MODES.includes(designMode)) {
      setDesignMode(DEFAULT_DESIGN_MODE);
    }
  }, [isAdmin, designMode, setDesignMode]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const theme = useTheme();
  const { designMode } = useDesignMode();
  const isDark = isDarkDesignMode(designMode);
  const dataTheme = isDark ? 'dark' : 'light';
  return (
    <div
      data-theme={dataTheme}
      data-corner-style={theme.cornerStyle}
      style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.textPrimary,
      fontFamily: theme.fontFamily ?? 'inherit',
      display: 'flex',
      flexDirection: 'column',
      ...(themeCornerIsChamfer(theme)
        ? {
            ['--chamfer-cut' as string]: theme.chamferCut ?? '7px',
            ['--chamfer-cut-btn' as string]: theme.chamferCutButton ?? '5px',
            ['--chamfer-cut-sm' as string]: theme.chamferCutMicro ?? '3px',
          }
        : {}),
    }}>
      <DesignModeAccessGuard />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: '100vh',
        }}
      >
      <Navbar />
      <GlobalTickerBanner />
      <WelcomeModal />
      <main style={{ flex: 1 }}>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><LoadingSpinner /></div>}>
        <Routes key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/explore" element={<RequireNoWallet><PublicPortal /></RequireNoWallet>} />
          <Route path="/docs" element={<Docs />} />
          {/* Legacy URL aliases preserved for old bookmarks / shared links. */}
          <Route path="/createauction" element={<Navigate to="/create" replace />} />
          <Route path="/createmarket" element={<Navigate to="/create" replace />} />
          <Route path="/auction/:id" element={<LegacyMarketRedirect />} />
          <Route path="/history" element={<Navigate to="/" replace />} />
          <Route path="/create" element={<RequireWallet><CreateMarket /></RequireWallet>} />
          {/* Public: browse detail without a wallet; vote controls show a connect overlay. */}
          <Route path="/market/:id" element={<MarketDetail />} />
          <Route path="/profile" element={<RequireWallet><Profile /></RequireWallet>} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/diagnose" element={<RequireWallet><Diagnose /></RequireWallet>} />
          <Route path="/daily/:day" element={<DailyDigest />} />
          {/* Public 404: unknown URLs must not redirect via RequireWallet (disconnected users would lose the 404). */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      </div>
    </div>
  );
}
function App() {
  return (
    <DesignModeProvider>
      <RainbowKitThemeProvider>
        <ThemeProvider>
          <HomeViewModeProvider>
            <Router>
              <LanguagePreferenceProvider>
                <AppContent />
              </LanguagePreferenceProvider>
            </Router>
          </HomeViewModeProvider>
        </ThemeProvider>
      </RainbowKitThemeProvider>
    </DesignModeProvider>
  );
}

export default App; 
